import type * as React from 'react';

import * as api from '@gitbook/api-client';
import { filterOutNullable } from '@gitbook/util-ts';

import {
    IntegrationCard,
    type IntegrationCardKind,
    IntegrationCardSkeleton,
} from './IntegrationCard';

/**
 * A list of integrations cards recommended for an org based on its use case.
 */
export function FeaturedIntegrations(props: {
    kind?: IntegrationCardKind;
    title: React.ReactNode;
    integrations: api.Integration[];
    organizationId: string;
    className?: string;
}): React.ReactElement | null {
    const { integrations, kind = 'default', organizationId } = props;

    if (integrations.length === 0) {
        return null;
    }

    return (
        <div className="flex w-full flex-1 flex-col gap-4">
            {props.title}

            <div className={props.className}>
                {integrations.map((integration) => (
                    <IntegrationCard
                        key={integration.name}
                        kind={kind}
                        integration={integration}
                        organizationId={organizationId}
                        featured
                    />
                ))}
            </div>
        </div>
    );
}

export function FeaturedIntegrationsSkeleton(props: { count: number; className?: string }) {
    return (
        <div className={props.className}>
            {Array.from({ length: props.count }).map((_, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                <IntegrationCardSkeleton key={index} kind="enhanced" />
            ))}
        </div>
    );
}

export function useFeaturedIntegrations(props: {
    integrations?: api.Integration[];
    // Integrations installed on the current space/site or on the org
    installedIntegrations?: string[];
    useCase?: api.OrganizationUseCase;
    limit?: number;
}): api.Integration[] {
    const { integrations, installedIntegrations, useCase, limit } = props;

    if (!integrations || integrations.length === 0) {
        return [];
    }

    const featuredIntegrationsNames = getFeaturedIntegrationsForUseCase({
        exclude: installedIntegrations,
        useCase,
    });

    const integrationsToFeature: api.Integration[] = featuredIntegrationsNames
        .map((integrationName) =>
            integrations.find((integration) => integration.name === integrationName)
        )
        .filter(filterOutNullable);

    const featuredIntegrationsToDisplay = limit
        ? integrationsToFeature.slice(0, limit)
        : integrationsToFeature;

    return featuredIntegrationsToDisplay;
}

function getFeaturedIntegrationsForUseCase(props: {
    useCase?: api.OrganizationUseCase;
    /** Integrations to exclude */
    exclude?: string[];
}): string[] {
    const { useCase, exclude } = props;
    let integrations: string[];

    switch (useCase) {
        case api.OrganizationUseCase.InternalDocs: {
            integrations = [
                'linear',
                'jira',
                'github',
                'lucid',
                'figma',
                'slack',
                'mermaid',
                'discord',
                'sentry',
                'mailchimp',
                'formspree',
                'arcade',
            ];
            break;
        }
        case api.OrganizationUseCase.DocsSite: {
            integrations = [
                'googleanalytics',
                'intercom',
                'hotjar',
                'plausible',
                'VA-Auth0',
                'segment',
                'posthog',
                'VA-Okta',
                'koala',
                'heap',
                'mixpanel',
                'helpscout',
                'cognito',
                'VA-Azure',
                'oidc',
            ];
            break;
        }
        case api.OrganizationUseCase.AudienceControlledSite: {
            integrations = [
                'VA-Okta',
                'VA-Auth0',
                'cognito',
                'VA-Azure',
                'oidc',
                'googleanalytics',
                'intercom',
                'hotjar',
                'plausible',
                'segment',
                'posthog',
                'mixpanel',
                'helpscout',
                'koala',
                'heap',
            ];
            break;
        }
        default: {
            integrations = [
                'slack',
                'github',
                'googleanalytics',
                'linear',
                'figma',
                'VA-Auth0',
                'mermaid',
                'jira',
                'lucid',
                'discord',
                'sentry',
                'mailchimp',
                'formspree',
                'arcade',
                'intercom',
                'hotjar',
                'segment',
                'posthog',
            ];
            break;
        }
    }

    return exclude?.length
        ? integrations.filter((integration) => !exclude.includes(integration))
        : integrations;
}
